import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, onMounted, ref, watch } from "vue";
import { VersionSchema, VersionSelectorTableProps } from "@/types";
import { actionsColumn, versionColumns } from "@/util";
import { getVersionApiStore, projectStore, permissionStore } from "@/hooks";
import { SelectorTable } from "@/components/common";
import { CreateVersionModal } from "@/components/project/creator";


/**
 * A table for selecting project versions.
 */
const __default__ = {
  name: "VersionSelectorTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    hideCurrentVersion: { type: Boolean },
    open: { type: Boolean },
    minimal: { type: Boolean },
    disabled: { type: Boolean },
    project: {}
  },
  emits: ["selected"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const selected = ref<VersionSchema | undefined>();
const addOpen = ref(false);
const versions = ref<VersionSchema[]>([]);

const selectedItems = computed({
  get() {
    return selected.value ? [selected.value] : [];
  },
  set(items: VersionSchema[]) {
    selected.value = items[0];
    emit("selected", items[0]);
  },
});

const columns = computed(() =>
  props.minimal ? versionColumns : [...versionColumns, actionsColumn]
);

const rows = computed(() =>
  props.hideCurrentVersion
    ? versions.value.filter(
        ({ versionId }) => versionId !== projectStore.versionId
      )
    : versions.value
);

const addable = computed(() =>
  permissionStore.isAllowed("project.edit_versions", props.project)
);
const deletable = computed(() => addable.value && versions.value.length > 1);

/**
 * Loads project versions.
 */
function handleReload() {
  getVersionApiStore.handleLoadVersions(props.project.projectId, {
    onSuccess: (loadedVersions) => {
      versions.value = loadedVersions;

      if (loadedVersions.length === 1 && props.minimal) {
        emit("selected", loadedVersions[0]);
      }
    },
  });
}

/**
 * Closes the add version modal and adds the created version.
 */
function handleAdd(version: VersionSchema) {
  versions.value = [version, ...versions.value];
  addOpen.value = false;
}

/**
 * Attempts to delete the version.
 * @param version - The version to delete.
 */
function handleDelete(version: VersionSchema) {
  getVersionApiStore.handleDelete(version, {
    onSuccess: () => {
      versions.value = versions.value.filter(
        ({ versionId }) => versionId != version.versionId
      );
    },
  });
}

onMounted(() => handleReload());

watch(
  () => props.project,
  () => handleReload()
);

watch(
  () => props.open,
  () => handleReload()
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(SelectorTable), {
    selected: selectedItems.value,
    "onUpdate:selected": _cache[1] || (_cache[1] = ($event: any) => ((selectedItems).value = $event)),
    minimal: props.minimal,
    addable: addable.value,
    deletable: deletable.value,
    loading: _unref(getVersionApiStore).getLoading,
    columns: columns.value,
    rows: rows.value,
    disabled: props.disabled ? true : undefined,
    "row-key": "projectId",
    "item-name": "version",
    icons: { add: 'version-add', edit: 'edit', delete: 'version-delete' },
    "data-cy": "table-version",
    onRefresh: handleReload,
    "onRow:add": _cache[2] || (_cache[2] = ($event: any) => (addOpen.value = true)),
    "onRow:delete": handleDelete
  }, {
    bottom: _withCtx(() => [
      _createVNode(_unref(CreateVersionModal), {
        open: addOpen.value,
        project: props.project,
        onClose: _cache[0] || (_cache[0] = ($event: any) => (addOpen.value = false)),
        onCreate: handleAdd
      }, null, 8, ["open", "project"])
    ]),
    _: 1
  }, 8, ["selected", "minimal", "addable", "deletable", "loading", "columns", "rows", "disabled"]))
}
}

})